import React from "react";
import { ClipboardCheck } from "lucide-react";
import AdminAPI from "../../services/AdminAPI";
import ApprovalsDashboard from "../../Components/approvals/ApprovalsDashboard";

const Chip = ({ children }) => (
  <span className="mr-1.5 mt-1 inline-block rounded-full border border-[#d9e7f7] bg-white px-2 py-0.5 text-[11px] text-[#1565c0]">{children}</span>
);

function AssessmentContent({ detail }) {
  const a = detail.assessment || {};
  const setup = a.setup || {};
  const questions = a.questions || [];
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-[#e6edf8] bg-white p-4">
        <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-4">
          {[["Grade", setup.grade], ["Subject", setup.subject || setup.domain], ["Type", setup.assessment_type], ["Total marks", setup.total_marks], ["Duration", setup.duration ? `${setup.duration} min` : ""], ["Topic", setup.topic]].map(([k, v]) => (
            <div key={k}><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{k}</div><div className="text-[13px] text-slate-800">{v || "—"}</div></div>
          ))}
        </div>
        {Array.isArray(setup.selected_competencies) && setup.selected_competencies.length > 0 && (
          <div className="mt-3"><span className="mr-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Competencies</span>{setup.selected_competencies.map((c, i) => <Chip key={i}>{typeof c === "string" ? c : c.name || c.competency || JSON.stringify(c)}</Chip>)}</div>
        )}
        {a.content?.instructions && <p className="mb-0 mt-3 whitespace-pre-wrap text-[12px] text-slate-600">{a.content.instructions}</p>}
      </div>
      <div className="text-[12px] font-semibold text-slate-600">{questions.length} question{questions.length === 1 ? "" : "s"}</div>
      {questions.map((q, i) => (
        <div key={q.id || i} className="rounded-xl border border-[#e6edf8] bg-white p-3">
          <div className="flex justify-between gap-3 text-[13px] font-semibold text-slate-800"><span>{i + 1}. {q.question_text}</span><span className="shrink-0 text-[11px] font-medium text-slate-500">{q.marks ?? 1} mark{(q.marks ?? 1) === 1 ? "" : "s"} · {q.type}</span></div>
          {(q.options || []).length > 0 && (
            <ul className="mt-1.5 list-none space-y-0.5 pl-0 text-[12px] text-slate-700">
              {q.options.map((o, oi) => <li key={oi} className={o.is_correct ? "font-semibold text-emerald-700" : ""}>{o.is_correct ? "✓ " : "○ "}{o.text || o.option_text || ""}</li>)}
            </ul>
          )}
          {q.correct_answer && <div className="mt-1.5 text-[12px] text-emerald-700">Answer: {q.correct_answer}</div>}
          {q.explanation && <div className="mt-1 text-[12px] text-slate-500">{q.explanation}</div>}
        </div>
      ))}
    </div>
  );
}

export const ASSESSMENT_APPROVALS_CONFIG = {
  entityTitle: "Assessments",
  title: "Assessment Approvals",
  subtitle: "Review teacher assessments before students can see them",
  entityLabel: "assessment",
  entityKey: "assessment",
  icon: ClipboardCheck,
  api: {
    queue: (p) => AdminAPI.approvalsQueue("assessments", p),
    detail: (id) => AdminAPI.approvalsDetail("assessments", id),
    review: (id, body) => AdminAPI.approvalsReview("assessments", id, body),
    bulk: (body) => AdminAPI.approvalsBulk("assessments", body),
    unpublish: (id) => AdminAPI.approvalsUnpublish("assessments", id),
  },
  titleOf: (r) => r.title,
  subtitleOf: (r) => [r.grade, r.subject].filter(Boolean).join(" · ") || "Assessment",
  columns: [
    { key: "title", label: "Assessment", render: (r) => <span className="font-semibold text-slate-800">{r.title}</span> },
    { key: "class", label: "Grade / subject", render: (r) => [r.grade, r.subject].filter(Boolean).join(" · ") || "—" },
    { key: "teacher", label: "Teacher", render: (r) => r.teacher_name || <span className="text-slate-400">Unknown</span> },
    { key: "size", label: "Questions", render: (r) => `${r.question_count}${r.total_marks ? ` · ${r.total_marks} marks` : ""}` },
  ],
  renderContent: (detail) => <AssessmentContent detail={detail} />,
};

export default function AssessmentApprovals() {
  return <ApprovalsDashboard config={ASSESSMENT_APPROVALS_CONFIG} />;
}
